import { useMemo } from "react";
import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { GraphQLError } from "graphql/error/GraphQLError";
import { useSnackbar } from "@tecorien/mimic";
import { config } from "config";
import { fetchWithToken } from "./util";

const internalCreateClient = (errorLink: ApolloLink) => {
  return new ApolloClient({
    link: ApolloLink.from([
      errorLink,
      createHttpLink({
        uri: config.graphqlEndpoint,
        fetch: fetchWithToken,
      }),
    ]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "no-cache",
        errorPolicy: "all",
      },
      query: {
        fetchPolicy: "no-cache",
        errorPolicy: "all",
      },
      mutate: {
        errorPolicy: "all",
      },
    },
  });
};

export function useCreateClient() {
  const { push } = useSnackbar();

  return useMemo(() => {
    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        graphQLErrors
          .filter(error => !(error.extensions?.classification === "ValidationError" && (error.extensions?.path as string)?.startsWith("data.")))
          .reduce((acc, error) => {
            const errorKey = (error?.extensions?.classification as string) || "Error";
            if (!acc.has(errorKey)) {
              acc.set(errorKey, []);
            }
            acc.get(errorKey)!.push(error);
            return acc;
          }, new Map<string, GraphQLError[]>())
          .forEach((errors, key) =>
            push({
              type: "error",
              title: key,
              message: errors.map(({ message }) => message || JSON.stringify(message, null, 2))
                .join(",\n"),
            }),
          );
      }

      if (networkError) {
        if (networkError?.message === "The user aborted a request.") {

        } else if (networkError?.message === "Operation aborted by client") {

        } else {
          push({
            type: "error",
            title: "Network error",
            message: networkError?.message || JSON.stringify(networkError, null, 2),
          });
        }
      }
    });
    return internalCreateClient(errorLink);
  }, [ push ]);
}
